import { useState } from "react";
import { FlaskConical, MessageCircle, Palette } from "lucide-react";
import { STORE, waLink } from "../data/store";

const FINISHES = ["Mate", "Satinado", "Brillante", "Poliuretano 2K", "Epoxi"];

const PRESETS = [
  { name: "Borgoña (tendencia)", hex: "#6B1F2A" },
  { name: "Rojo Vermillion", hex: "#E02424" },
  { name: "Azul Sherwin", hex: "#0A3977" },
  { name: "Cian Eléctrico", hex: "#0067B1" },
  { name: "Amarillo Killing", hex: "#F59E0B" },
  { name: "Verde Esmeralda", hex: "#15803D" },
  { name: "Grafito", hex: "#374151" },
  { name: "Blanco Hueso", hex: "#F5F1E6" },
];

export const ColorLab = ({ hex, setHex }) => {
  const [finish, setFinish] = useState("Satinado");

  const message = `Hola ${STORE.name}, quiero preparar un color en el laboratorio.\nColor (HEX): ${hex}\nAcabado: ${finish}\n¿Pueden darme la fórmula y el precio?`;

  return (
    <section id="section-lab" className="scroll-mt-20 py-16 bg-[#0A3977] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10"
        style={{ background: "radial-gradient(circle at 15% 20%, #E02424, transparent 35%), radial-gradient(circle at 85% 30%, #F59E0B, transparent 35%)" }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <div className="flex items-center gap-2 text-[#F59E0B] font-bold text-xs tracking-widest uppercase mb-3">
          <FlaskConical className="w-4 h-4" /> Laboratorio de Color
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
          Preparación de colores computarizados y personalizados
        </h2>
        <p className="mt-3 text-blue-100 max-w-2xl">
          Elegí un color del catálogo o creá el tuyo. Simulá el acabado y enviános la fórmula por
          WhatsApp para que la preparemos al instante en nuestro laboratorio.
        </p>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* Mixer */}
          <div className="bg-white rounded-2xl p-6 text-gray-900 shadow-xl">
            <div className="flex items-center gap-4">
              <div
                data-testid="color-preview"
                className="w-24 h-24 rounded-xl border-4 border-white shadow-inner shrink-0"
                style={{ background: hex }}
              />
              <div className="flex-1">
                <label className="text-xs font-bold tracking-widest uppercase text-gray-500">Color personalizado</label>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="color" value={hex} data-testid="color-lab-custom-picker"
                    onChange={(e) => setHex(e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300 bg-white"
                  />
                  <input
                    value={hex} data-testid="color-hex-input"
                    onChange={(e) => setHex(e.target.value)}
                    className="font-mono-code text-lg font-bold uppercase flex-1 px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-[#0067B1]"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5">
              <label className="text-xs font-bold tracking-widest uppercase text-gray-500">Acabado</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {FINISHES.map((f) => (
                  <button
                    key={f} data-testid={`finish-${f}`}
                    onClick={() => setFinish(f)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                      finish === f ? "bg-[#0A3977] text-white border-[#0A3977]" : "bg-white text-gray-700 border-gray-300 hover:border-[#0067B1]"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <a
              data-testid="color-lab-whatsapp"
              href={waLink(message)} target="_blank" rel="noreferrer"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-bold hover:bg-[#1EBE5B] transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> Consultar fórmula por WhatsApp
            </a>
          </div>

          {/* Presets */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/15">
            <div className="flex items-center gap-2 font-bold mb-4">
              <Palette className="w-4 h-4 text-[#F59E0B]" /> Muestrario rápido
            </div>
            <div className="grid grid-cols-4 gap-3">
              {PRESETS.map((p) => (
                <button
                  key={p.hex} data-testid={`preset-${p.hex}`}
                  onClick={() => setHex(p.hex)}
                  className="group flex flex-col items-center gap-1.5"
                >
                  <span
                    className={`w-full aspect-square rounded-xl border-2 transition-transform group-hover:-translate-y-1 ${hex === p.hex ? "border-[#F59E0B]" : "border-white/40"}`}
                    style={{ background: p.hex }}
                  />
                  <span className="text-[11px] text-blue-100 text-center leading-tight">{p.name}</span>
                </button>
              ))}
            </div>
            <p className="mt-5 text-sm text-blue-100">
              Igualamos cualquier tono de abanico Sherwin-Williams o Killing, incluso muestras que
              traigas a la tienda.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
